import { ReactNode, Suspense, useEffect, useState } from "react";
import Loading from "../../../../common/components/ui/loading/Loading";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import { IProduct, TYPE_MEASUREDUNIT } from "../../../../types/product.types";
import { getProducts } from "../../../../common/services/product.api";
import { useNavigate } from "react-router-dom";
import ChangeMeasuredUnit from "../modals/ChangeMeasuredUnit";
import ModalRegisterProduct from "../modals/RegisterProduct";
import DeleteProduct from "../modals/DeleteProduct";
import UpdateProductModal from "../modals/UpdateProduct";

const STATUS_LABELS = {};

const headers = {
  code: "código do produto",
  name: "descrição",
  category: "Categoria",
  corrective: "Tipo de Peça",
  measuredunit: "Tipo de Unidade",
};

const EMPTY_ACTION = { eventName: "", data: undefined };

function TableProduct() {
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [products, setProducts] = useState<IProduct[]>([]);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );

  useEffect(() => {
    async function loadProducts(filters, page) {
      setIsLoading(true);
      const result = await getProducts({ ...filters, page });
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      setIsLoading(false);

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setProducts(result.data);
    }

    loadProducts(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, action]);

  const formatTypeProduct = (types: {
    Corretivo: boolean;
    Preventivo: boolean;
    "Mau Uso": boolean;
  }) => {
    const values = Object.values(types);
    let text = "";
    Object.keys(types).map((type, i) => {
      if (values.filter((value) => value).length > 1 && values[i]) {
        text = `${text} ${type}  |`;
      } else if (values[i]) {
        text = type;
      }
    });
    text = text[text.length - 1] == "|" ? text.slice(0, text.length - 1) : text;
    return text == "" ? "Geral" : text;
  };

  const actions = [
    {
      label: "Alterar tipo da peça",
      action: (data) => {
        setAction({ eventName: "edit", data: data });
      },
      permission: "create:products",
    },
    {
      label: "Editar Produto",
      action: (data) => {
        setAction({ eventName: "edit-product", data: data });
      },
      permission: "edit:products",
    },
    {
      label: "Deletar",
      action: (data) => {
        setAction({ eventName: "delete", data: data });
      },
      permission: "delete:products",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Produtos"
          placeholder="Digite o nome do produto ou o código."
          statusOptions={STATUS_LABELS}
          newItemLabel="Novo Produto"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          tableData={products}
          permission="create:products"
        />

        <GenericTable
          headers={headers}
          data={products}
          actions={actions}
          renders={{
            code: (value, data) =>
              (data as IProduct).referencesSet &&
              (data as IProduct).measuredunit != TYPE_MEASUREDUNIT.UNID ? (
                <a
                  href={`/produto/${(data as IProduct)._id as string}`}
                  onClick={(e) => {
                    e.preventDefault(); // Para evitar o comportamento padrão do link
                    navigate(`/produto/${(data as IProduct)._id}`); // Navegação na mesma página
                  }}
                  className="cursor-pointer"
                >
                  {value as ReactNode}
                </a>
              ) : (
                <p>{value as ReactNode}</p>
              ),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            corrective: (_: any, _data) => {
              const data = _data as IProduct;
              return (
                <p>
                  {formatTypeProduct({
                    "Mau Uso": data.misuse,
                    Corretivo: data.corrective,
                    Preventivo: data.preventive,
                  })}
                </p>
              );
            },
          }}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          entity="products"
        />
        {isLoading && <Loading />}
        {isAction("create") && (
          <ModalRegisterProduct
            onClose={cleanActionData}
            onSave={() => {
              cleanActionData();
              setFilters((prev) => ({ ...prev }));
            }}
          />
        )}

        {isAction("edit-product") && (
          <UpdateProductModal
            productId={(action.data as IProduct)._id}
            onClose={cleanActionData}
            onSave={() => {
              cleanActionData();
              setFilters((prev) => ({ ...prev }));
            }}
          />
        )}

        {isAction("edit") && (
          <ChangeMeasuredUnit
            productId={(action.data as IProduct)._id}
            measuredunit={(action.data as IProduct).measuredunit}
            onClose={cleanActionData}
          ></ChangeMeasuredUnit>
        )}
        {isAction("delete") && (
          <DeleteProduct
            productId={(action.data as IProduct)._id}
            code={(action.data as IProduct).code}
            onClose={cleanActionData}
            onSave={() => {
              cleanActionData();
              setFilters((prev) => ({ ...prev }));
            }}
          />
        )}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default TableProduct;
